/**
 * SubchatPanel - Slide-over panel for a subchat branched from a selection in
 * a parent conversation's assistant message. Streams and persists exactly like
 * a regular conversation (same wsClient events, filtered by its own
 * conversationId) but never appends its messages back into the parent thread.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChatInterface } from './ChatInterface';
import { wsClient } from '../services/websocket';
import { apiClient } from '../services/api';
import type { Message, Model, WSServerMessage } from '../types';

export interface SubchatPanelProps {
  subchatConversationId: string;
  sourceExcerpt: string;
  modelId: string;
  models?: Model[];
  onClose: () => void;
}

export const SubchatPanel: React.FC<SubchatPanelProps> = ({
  subchatConversationId,
  sourceExcerpt,
  modelId,
  models = [],
  onClose,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const streamingContentRef = useRef<string>('');

  // Load existing messages if this subchat was created earlier (e.g. panel reopened)
  useEffect(() => {
    let cancelled = false;

    apiClient
      .getConversation(subchatConversationId)
      .then((conversation) => {
        if (!cancelled) setMessages(conversation.messages);
      })
      .catch((err) => {
        console.error('Failed to load subchat:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [subchatConversationId]);

  const handleTokenReceived = useCallback((token: string) => {
    streamingContentRef.current += token;

    setMessages((prevMessages) => {
      const lastMessage = prevMessages[prevMessages.length - 1];

      if (lastMessage && lastMessage.role === 'assistant' && lastMessage.id.startsWith('streaming-')) {
        return [
          ...prevMessages.slice(0, -1),
          { ...lastMessage, content: streamingContentRef.current },
        ];
      }

      const newMessage: Message = {
        id: `streaming-${Date.now()}`,
        role: 'assistant',
        content: streamingContentRef.current,
        modelId,
        timestamp: new Date().toISOString(),
        tokenCount: 0,
      };
      return [...prevMessages, newMessage];
    });
  }, [modelId]);

  const handleStreamComplete = useCallback((messageId: string, tokenCount: number) => {
    const finalContent = streamingContentRef.current;
    setIsStreaming(false);

    setMessages((prevMessages) => {
      const lastMessage = prevMessages[prevMessages.length - 1];
      if (lastMessage && lastMessage.role === 'assistant') {
        return [
          ...prevMessages.slice(0, -1),
          { ...lastMessage, id: messageId, tokenCount, content: finalContent },
        ];
      }
      return prevMessages;
    });

    streamingContentRef.current = '';
    setError(null);
  }, []);

  const handleStreamError = useCallback((errorMessage: string, code: string) => {
    setIsStreaming(false);
    streamingContentRef.current = '';
    setError(`Error (${code}): ${errorMessage}`);

    setMessages((prevMessages) => {
      const lastMessage = prevMessages[prevMessages.length - 1];
      if (lastMessage && lastMessage.role === 'assistant' && lastMessage.id.startsWith('streaming-')) {
        return prevMessages.slice(0, -1);
      }
      return prevMessages;
    });
  }, []);

  useEffect(() => {
    const handleMessage = (message: WSServerMessage) => {
      if ('conversationId' in message && message.conversationId !== subchatConversationId) {
        return;
      }

      switch (message.type) {
        case 'token':
          handleTokenReceived(message.token);
          break;
        case 'complete':
          handleStreamComplete(message.messageId, message.tokenCount);
          break;
        case 'error':
          handleStreamError(message.error, message.code);
          break;
        default:
          break;
      }
    };

    const unsubscribe = wsClient.onMessage(handleMessage);
    return unsubscribe;
  }, [subchatConversationId, handleTokenReceived, handleStreamComplete, handleStreamError]);

  const handleSendMessage = useCallback(
    (content: string) => {
      if (!content.trim() || isStreaming) return;

      const userMessage: Message = {
        id: `user-${Date.now()}`,
        role: 'user',
        content,
        modelId,
        timestamp: new Date().toISOString(),
        tokenCount: 0,
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsStreaming(true);
      setError(null);
      streamingContentRef.current = '';

      try {
        wsClient.sendMessage(subchatConversationId, content, modelId);
      } catch (err) {
        setIsStreaming(false);
        setError(err instanceof Error ? err.message : 'Failed to send message');
      }
    },
    [subchatConversationId, modelId, isStreaming]
  );

  const handleCancelStream = useCallback(() => {
    if (!isStreaming) return;
    try {
      wsClient.cancelStream(subchatConversationId);
      setIsStreaming(false);
      streamingContentRef.current = '';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel stream');
    }
  }, [subchatConversationId, isStreaming]);

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-[480px] h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col">
        <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
              💬 Subchat
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400"
              aria-label="Close subchat"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <blockquote className="mt-2 border-l-2 border-blue-300 dark:border-blue-700 pl-3 text-xs text-gray-500 dark:text-gray-400 italic line-clamp-3">
            {sourceExcerpt}
          </blockquote>
        </div>

        {error && (
          <div className="mx-4 mt-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-2 rounded-lg text-sm flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        <div className="flex-1 overflow-hidden">
          <ChatInterface
            conversationId={subchatConversationId}
            messages={messages}
            currentModelId={modelId}
            isStreaming={isStreaming}
            onSendMessage={handleSendMessage}
            onCancelStream={handleCancelStream}
            models={models}
          />
        </div>
      </div>
    </div>
  );
};
